const { reactive, provide, inject, ref, toRefs, onMounted, watch } = Vue;
const { useRoute } = VueRouter;

const shapeToImageUrl = {
  Round: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Round_Sample.png?v=1765972273",
  Oval: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Oval_Sample.png?v=1765972273",
  Cushion: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Cushion_Sample.png?v=1765972273",
  Princess: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Princess_Sample.png?v=1765972273",
  Radiant: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Radiant_Sample.png?v=1765972273",
  Pear: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Pear_Sample.png?v=1765972273",
  Marquise: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Marquise_Sample.png?v=1765972272",
  Emerald: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Emerald_Sample.png?v=1765972271",
  Asscher: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Asscher_Sample.png?v=1765972272",
  Heart: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/Heart_Sample.png?v=1765972273",
  DEFAULT: "https://cdn.shopify.com/s/files/1/0973/6614/7409/files/default_shopify_image.webp?v=1765972272",
};

/* Encodes the full setting/diamond payload (as already loaded in this session,
   including fields the Storefront API can't refetch — metafield-backed video,
   band width, prong type) into a URL-safe base64 string, so a final-ring link
   opened cold (another tab/device/QR scan) can render identically without
   re-fetching anything. Mirrors the encode/decode contract of the newer React
   ring-builder rewrite (ring-builder/src/hooks/useEncodeDecode.js). */
function encodeRingData(payload, encrypt = true) {
  try {
    if (!encrypt) return JSON.stringify(payload);
    return btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
  } catch (e) {
    console.error("encodeRingData failed:", e);
    return null;
  }
}

/* Escapes only the characters that would otherwise break query-string parsing
   (space, &, #, %), leaving quotes/braces/colons/slashes readable in the
   address bar — used for the plain-JSON ?data= debug format instead of
   URLSearchParams, which would percent-encode the entire value. */
function minimallyEscapeForUrl(raw) {
  return raw
    .replace(/%/g, "%25")
    .replace(/&/g, "%26")
    .replace(/#/g, "%23")
    .replace(/ /g, "%20");
}

function decodeRingData(encoded, encrypt = true) {
  try {
    if (!encrypt) return JSON.parse(encoded);
    return JSON.parse(decodeURIComponent(escape(atob(encoded))));
  } catch (e) {
    console.error("decodeRingData failed:", e);
    return null;
  }
}

/* The cached selectedSetting object carries the full raw Storefront product
   (image gallery, every variant/option, media nodes) — encoding all of that
   into a URL blows past request-URI size limits (nginx 414). Everything
   dropped here (images, variants, options, media) is cheaply reconstructed by
   buildSettingFromVariant()'s API refetch given just the id (shapeVariants by
   the final page's syncSettingVariantToDiamondShape()); fields the fetch
   can't rebuild — attributes, attribute_variations, variations,
   matching_band, tags, settingVideo/bandWidth/prongType — are kept as-is.
   Diamond objects don't need this treatment: getDiamondData() already
   refetches every field on a cached diamond. */
function trimSettingForUrl(setting) {
  if (!setting) return setting;
  var {
    variants,
    options,
    media,
    productVideosMetafield,
    shapeVariants,
    image_gallery,
    main_image,
    featured_image,
    ...kept
  } = setting;
  return kept;
}

function getShapeFallbackImage(shape) {
  if (!shape) return shapeToImageUrl.DEFAULT;
  var key = shape.charAt(0).toUpperCase() + shape.slice(1).toLowerCase();
  return shapeToImageUrl[key] || shapeToImageUrl.DEFAULT;
}

/* reset ring at initial stage */
const resetRingFromSession = function (select = undefined) {
  /* remove settings applied filters, step data and stiky bar data from session */
  if (select == undefined) {
    sessionStorage.removeItem("stepData");
    sessionStorage.removeItem("selectedSetting");
    sessionStorage.removeItem("selectedDiamond");
    sessionStorage.removeItem("selectedSettingOptions");
    sessionStorage.removeItem("chooseSettingsAppliedFilters");
    sessionStorage.removeItem("stickyBarItems");
    sessionStorage.removeItem("selectedSecondDiamond");
    sessionStorage.removeItem("searchingSecondDiamond");
    sessionStorage.removeItem("selectedMatchingBand");

    /* remove selected setting , setting configuration and selected diamond from session */
    /* remove diamond applied filters and cached paginated data from session */
    removeDiamondSessionData();
    /* remove setting cached paginated data */
    removeSettingPaginatedData();
    /* remove viewed setting and diamond id from session */
    sessionStorage.removeItem("settingDetailPageViewedId");
    sessionStorage.removeItem("isDiamondDetailPageViewed");
  }
};

const removeSettingPaginatedData = () => {
  var settingsSessionFields = ["List", "CurrentPage", "Count"];
  for (var settingsSessionField of settingsSessionFields) {
    sessionStorage.removeItem(`setting${settingsSessionField}`);
  }
};

const removeDiamondSessionData = () => {
  var diamondTypes = ["white", "lab","white_color","lab_color", "fancy"];
  for (var diamondType of diamondTypes) {
    /*remove diamonds applied filters */
    sessionStorage.removeItem(`${diamondType}DiamondsAppliedFilters`);
    /* remove diamond list, current page and total diamond count from session */
    sessionStorage.removeItem(`${diamondType}DiamondList`);
    sessionStorage.removeItem(`${diamondType}CurrentPage`);
    sessionStorage.removeItem(`${diamondType}DiamondCount`);
  }
};

/* PC breadcrumbs */
const BreadcrumbsComp = {
  props: ["urlList"],
  delimiters: ["[[", "]]"],
  template: `#pc-breadcrumbs`,
};

/* Diamond sold-out popup — shown when a selected diamond is no longer available in the feed.
   `diamond` may be a feed object or a partial { diamondId, diamondType }. */
const DiamondSoldOutPopup = {
  props: ["open", "diamond"],
  delimiters: ["[[", "]]"],
  template: `#diamond-sold-out-popup`,
  computed: {
    diamondLabel() {
      return window.ROADiamond
        ? window.ROADiamond.describeDiamond(this.diamond)
        : "The selected diamond";
    },
  },
  methods: {
    findAnother() {
      var type = this.diamond && this.diamond.diamondType;
      // Land on the shape that just sold out, not the unfiltered listing.
      var shape = window.ROADiamond
        ? window.ROADiamond.diamondShapeOf(this.diamond)
        : "";
      window.location.href = window.ROADiamond
        ? window.ROADiamond.typeToSearchUrl(type, shape)
        : "/collections/natural-diamonds";
    },
  },
};

/* Diamond already-in-cart popup — a diamond is one of a kind, so a second add is
   never a quantity bump; show the shopper what is already in the bag instead.
   `item` is the matching /cart.js line (preferred, it is what the cart will show)
   and `diamond` the feed object being added, used as the fallback. */
const DiamondInCartPopup = {
  props: ["open", "item", "diamond"],
  delimiters: ["[[", "]]"],
  emits: ["close"],
  template: `#diamond-in-cart-popup`,
  computed: {
    itemTitle() {
      var item = this.item || {};
      if (item.product_title) return item.product_title;
      var diamond = this.diamond;
      return diamond ? getDiamondTitle(diamond, diamond.diamondType) : "This diamond";
    },
    itemImage() {
      var item = this.item || {};
      var diamond = this.diamond || {};
      return (
        item.image ||
        (item.featured_image && item.featured_image.url) ||
        diamond.diamondImage ||
        getShapeFallbackImage(diamond.shape)
      );
    },
    certificateLabel() {
      var props = (this.item && this.item.properties) || {};
      var diamond = this.diamond || {};
      var cert =
        props.certificate_number ||
        props.sku ||
        diamond.certificateNumber ||
        diamond.diamondId;
      return cert ? `Diamond Certificate: ${cert}` : "";
    },
  },
  methods: {
    /* Same staged fallback the detail page uses: feed image -> shape line art
       -> generic diamond, so a dead CDN URL never leaves an empty box. */
    onImageError(e) {
      var target = e.target;
      var stage = parseInt(target.dataset.imgErrorStage || "0");
      target.dataset.imgErrorStage = String(stage + 1);
      if (stage === 0) {
        target.setAttribute(
          "src",
          getShapeFallbackImage(this.diamond && this.diamond.shape)
        );
      } else if (stage === 1) {
        target.setAttribute("src", shapeToImageUrl.DEFAULT);
      }
    },
    close() {
      this.$emit("close");
    },
    viewCart() {
      window.location.href = "/cart";
    },
  },
};

/* changed: { label, diamondType, lineKey, diamondId, previousPrice, updatedPrice, properties }
   previousPrice/updatedPrice are in cents (matches Shopify's cart line `price` field).
   Closing (via @close) is the only action — the parent performs the background cart sync. */
const DiamondPriceChangePopup = {
  props: ["open", "changed", "currencySymbol"],
  delimiters: ["[[", "]]"],
  template: `#diamond-price-change-popup`,
  computed: {
    previousPriceLabel() {
      return this.formatPrice(this.changed && this.changed.previousPrice);
    },
    updatedPriceLabel() {
      return this.formatPrice(this.changed && this.changed.updatedPrice);
    },
  },
  methods: {
    formatPrice(cents) {
      var amount = (Number(cents) || 0) / 100;
      return (
        (this.currencySymbol || "$") +
        amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      );
    },
    close() {
      this.$emit("close");
    },
  },
};

/* set meta data */
const setMetaData = (rootMetaDataElement, metaDataObjectArray) => {
  var headElement = document.head;
  var childNodes = headElement.children;
  for (var metaObject of metaDataObjectArray) {
    var metaDataObjectEntry = Object.entries(metaObject);
    for (i = 0; i < childNodes.length; i++) {
      if (
        childNodes[i].tagName.toLowerCase() == rootMetaDataElement &&
        childNodes[i].getAttribute(metaDataObjectEntry[0][0]) ==
        metaDataObjectEntry[0][1]
      ) {
        headElement.removeChild(childNodes[i]);
      }
    }
    var metaElement = document.createElement(rootMetaDataElement);
    metaElement.setAttribute(
      metaDataObjectEntry[0][0],
      metaDataObjectEntry[0][1]
    );
    metaElement.setAttribute(
      metaDataObjectEntry[1][0],
      metaDataObjectEntry[1][1]
    );
    headElement.appendChild(metaElement);
  }
};

/* get page title common for all detail pages */
const getDiamondTypeLabel = (diamondType) => {
  switch (diamondType) {
    case "lab":
      return "Lab Grown";
      break;
    case "white":
      return "Natural";
      break;
    case "lab_color":
      return "Lab Grown";
      break;
    case "white_color":
      return "Natural";
      break;
    case "fancy":
      return "Coloured";
      break;
    default:
      return "";
      break;
  }
};

const getDiamondTitle = (diamond, diamondType) => {
  var typeLabel = "";
  if (diamondType) {
    var dt = diamondType.toLowerCase();
    if (dt == "lab_color" || dt.includes("coloured-lab-grown")) {
      typeLabel = " Coloured Lab Grown";
    } else if (dt == "lab" || dt.includes("lab")) {
      typeLabel = " Lab Grown";
    } else if (dt == "white_color" || dt.includes("natural-fancy") || dt.includes("natural-color")) {
      typeLabel = " Coloured Natural";
    } else {
      typeLabel = " Natural";
    }
  }
  var fancyColor = diamond.fancyColor ? diamond.fancyColor.charAt(0).toUpperCase() + diamond.fancyColor.slice(1).toLowerCase() : "";
  var parts = [];
  if (diamond.caratWeight) parts.push(parseFloat(diamond.caratWeight) + " Carat");
  if (diamond.shape) parts.push(diamond.shape);
  if (fancyColor) parts.push(fancyColor);
  if (typeLabel) parts.push(typeLabel.trim());
  parts.push("Diamond");
  return parts.join(" ");
};

const getGemstoneSampleImg = (diamond) => {
  return `${baseAssetsUrl}/${diamond.stoneType
    .split(" ")
    .join("_")
    .toLowerCase()}/${diamond.shape.toLowerCase()}.jpg`;
};

/* change option confirmation popup */
const ChangeOptionPopupComp = {
  props: [
    "showPopup",
    "popupTitle",
    "popupImg",
    "popupPlaceholder",
    "popupType",
    "popupWarningMsg",
  ],
  emits: ["changeCurrentOption"],
  delimiters: ["[[", "]]"],
  template: `#change-option-confirm-popup`,
  methods: {
    setPlaceholderImg(e, popupType, placeholder) {
      if (popupType == "setting") {
        e.target.setAttribute("src", placeholder);
      } else {
        var imgUrl = `${window.location.origin}/cdn/shop/t/9/assets/${placeholder}.png`;
        e.target.setAttribute("src", imgUrl);
      }
    },
  },
};

/* PC left items sticky bar */
const StickyBarComp = {
  props: ["stickyItems"],
  data() {
    return {
      showStickyBar: false,
    };
  },
  delimiters: ["[[", "]]"],
  template: `#left-sticky-bar`,
  computed: {
    getInitialRingItem() {
      var ringItems = this.$props.stickyItems;
      if (ringItems.length == 1) {
        return `pc-${ringItems[0].page}-img`;
      } else {
        return `pc-ring-img`;
      }
    },
  },
  mounted() {
    this.handleStickyBarClass();
    this._onDocClick = (e) => {
      if (!this.showStickyBar) return;
      var sidebar = document.getElementById("sticky-items-sidebar");
      if (sidebar && !sidebar.contains(e.target)) {
        this.showStickyBar = false;
        this.handleStickyBarClass();
      }
    };
    document.addEventListener("click", this._onDocClick);
  },
  beforeUnmount() {
    if (this._onDocClick) {
      document.removeEventListener("click", this._onDocClick);
    }
  },
  methods: {
    setFormattedPrice(price) {
      return price.split(".")[0];
    },
    adjustPriceByRingSize(ringSize, price) {
      return price;
      // Define ring sizes in order and the base size for comparison
      const ringSizes = [
        "Select Ring Size",
        "E",
        "E 1/2",
        "F",
        "F 1/2",
        "G",
        "G 1/2",
        "H",
        "H 1/2",
        "I",
        "I 1/2",
        "J",
        "J 1/2",
        "K",
        "K 1/2",
        "L",
        "L 1/2",
        "M",
        "M 1/2",
        "N",
        "N 1/2",
        "O",
        "O 1/2",
        "P",
        "P 1/2",
        "Q",
        "Q 1/2",
        "R",
        "R 1/2",
        "S",
        "S 1/2",
        "T",
        "T 1/2",
        "U",
        "U 1/2",
        "V",
        "V 1/2",
        "W",
        "W 1/2",
        "X",
        "X 1/2",
        "Y",
        "Y 1/2",
        "Z",
        "Z 1/2",
      ];
      const baseSize = "N"; // Base size threshold for price adjustment

      // Check if price is a string and clean up formatting if necessary
      let numericPrice =
        typeof price === "string"
          ? parseFloat(price.replace(/[^0-9.]/g, ""))
          : price;

      // Ensure the price is a valid number
      if (isNaN(numericPrice)) {
        // console.error("Invalid price input");
        return null; // Handle invalid price as needed
      }

      // Find indexes for the selected ring size and base size
      const ringSizeIndex = ringSizes.indexOf(ringSize);
      const baseSizeIndex = ringSizes.indexOf(baseSize);

      // Adjust the price if the ring size index is greater than or equal to the base size index
      return ringSizeIndex >= baseSizeIndex ? numericPrice + 20 : numericPrice;
    },
    slideStickyBar() {
      this.showStickyBar = !this.showStickyBar;
      this.handleStickyBarClass();
    },
    handleStickyBarClass() {
      if (this.showStickyBar) {
        document
          .getElementById("sticky-items-sidebar")
          .classList.add("pc-sticky-active");
      } else {
        document
          .getElementById("sticky-items-sidebar")
          .classList.remove("pc-sticky-active");
      }
    },
    setPlaceholderImg(e) {
      var placeholder = e.target.getAttribute("placeholder");
      var pageType = e.target.getAttribute("page-type");
      var imgUrl = "";
      if (pageType == "diamond") {
        imgUrl = `${window.location.origin}/cdn/shop/t/9/assets/${placeholder}.png`;
      } else if (placeholder) {
        imgUrl = placeholder;
      }
      if (imgUrl) {
        e.target.setAttribute("src", imgUrl);
      } else {
        e.target.style.display = "none";
      }
    },
    closeStickyBar() {
      this.showStickyBar = false;
      this.handleStickyBarClass();
    },
    viewDetailPage(itemId, pageType, diamondType = "", stickyItem) {
      this.closeStickyBar();
      if (pageType == "setting") {
        window.location.href = `/collections/engagement-rings/products/${stickyItem.handle}?variant=${itemId}`;
      } else if (pageType == "diamond") {
        var collectionPath = !diamondType ? "natural-diamonds"
          : diamondType == "white" ? "natural-diamonds"
          : diamondType == "lab" ? "lab-diamonds"
          : diamondType == "white_color" ? "natural-fancy-coloured-diamonds"
          : diamondType == "lab_color" ? "coloured-lab-grown-diamonds"
          : "natural-diamonds";
        window.location.href = `/collections/${collectionPath}/diamonds?diamondId=${itemId}`;
      }
    },
  },
};

/* ── Sticky Mobile Action Bar ──────────────────────────────────────────────
 * Watches an element (observeTargetId) via IntersectionObserver.
 * When that element scrolls out of view the bar slides up from the bottom.
 * Uses a default slot so each page provides its own button markup with full
 * access to the parent component's data / methods.
 * ─────────────────────────────────────────────────────────────────────── */
const StickyMobileBarComp = {
  delimiters: ['[[', ']]'],
  template: `
    <div
      class="roa-sticky-bar"
      ref="bar"
      :class="{ 'roa-sticky-bar--active': isVisible }"
      v-if="isMobile"
    >
      <div class="roa-sticky-bar__btns">
        <slot></slot>
      </div>
    </div>
  `,
  props: {
    observeTargetId: { type: String, required: true },
  },
  data() {
    return {
      isVisible: false,
      isMobile: typeof window !== 'undefined' && window.innerWidth <= 768,
      _lastVisible: null,
      _observer: null,
    };
  },
  mounted() {
    if (!this.isMobile) return;
    this.$nextTick(() => this._initObserver());
    window.addEventListener('scroll', this._onScroll, { passive: true });
  },
  beforeUnmount() {
    if (this._observer) this._observer.disconnect();
    window.removeEventListener('scroll', this._onScroll);
    this._applyVisible(false);
  },
  methods: {
    _initObserver() {
      var target = document.getElementById(this.observeTargetId);
      if (!target) { setTimeout(() => this._initObserver(), 500); return; }
      this._observer = new IntersectionObserver(
        (entries) => { this._applyVisible(!entries[0].isIntersecting); },
        { threshold: 0 }
      );
      this._observer.observe(target);
    },
    _onScroll() {
      var target = document.getElementById(this.observeTargetId);
      if (!target) return;
      var r = target.getBoundingClientRect();
      this._applyVisible(!(r.top < window.innerHeight && r.bottom > 0));
    },
    _applyVisible(visible) {
      if (this._lastVisible === visible) return;
      this._lastVisible = visible;
      this.isVisible = visible;
      document.body.classList.toggle('roa-sticky-bar-active', visible);
      this.$nextTick(() => {
        var barEl = this.$refs.bar;
        var offset = visible && barEl ? barEl.offsetHeight : 0;
        document.documentElement.style.setProperty('--roa-sticky-h', offset + 'px');
      });
    },
  },
};

/* Safe sessionStorage JSON parser */
function safeParseJSON(key, fallback) {
  try {
    var raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : (fallback !== undefined ? fallback : null);
  } catch (e) {
    return fallback !== undefined ? fallback : null;
  }
}

/* Single source of truth for building a final-ring URL, so every redirect into
   final-ring (the "Complete Your Ring" buttons on the setting/diamond pages, the
   header steps bar's "View" link/click, etc.) produces the exact same URL shape —
   a single opaque ?data=<encoded payload> param (see encodeRingData above), no
   plaintext settingId/diamondId/etc. alongside it — regardless of which
   page/flow triggered it. The finalRingPage route's beforeEnter guard (see
   ring-builder.js) decodes this back into the query params the page component
   reads.
   ids is only used to short-circuit when the caller doesn't yet have a
   setting+diamond pair (e.g. thirdStepData not populated); the actual URL is
   always built from the cached selectedSetting/selectedDiamond session data.
   routeQuery is the calling component's this.$route.query, if any (preferred over
   sessionStorage for settingVideo since it's authoritative when already on final-ring). */
function buildFinalRingUrl(ids, routeQuery, encrypt = true) {
  if (!ids || !ids.settingId || !ids.diamondId) return null;
  var settingOptions = safeParseJSON("selectedSettingOptions");
  var settingVideo = routeQuery?.settingVideo || sessionStorage.getItem("selectedSettingVideo");
  var matchingBand = safeParseJSON("selectedMatchingBand");
  var secondDiamond = safeParseJSON("selectedSecondDiamond");
  var cachedSetting = safeParseJSON("selectedSetting");
  var cachedDiamond = safeParseJSON("selectedDiamond");
  if (!cachedSetting || !cachedDiamond) return null;
  var fullPayloadEncoded = encodeRingData({
    setting: {
      ...trimSettingForUrl(cachedSetting),
      settingSize: settingOptions?.settingSize,
      settingMetal: settingOptions?.settingMetal,
      settingVideo: settingVideo || cachedSetting.settingVideo || null,
    },
    diamond: cachedDiamond,
    matchingBandId: matchingBand?.id || null,
    secondDiamond: secondDiamond?.diamondId
      ? { diamondId: secondDiamond.diamondId, diamondType: secondDiamond.diamondType || "" }
      : null,
  }, encrypt);
  if (!fullPayloadEncoded) return null;
  if (!encrypt) {
    return `/collections/final-ring?data=${minimallyEscapeForUrl(fullPayloadEncoded)}`;
  }
  var params = new URLSearchParams({ data: fullPayloadEncoded });
  return `/collections/final-ring?${params.toString()}`;
}

/* PC header steps bar */
const HeaderStepsComp = {
  props: [
    "firstStepData",
    "secondStepData",
    "thirdStepData",
    "totalSteps",
    "redirectPage",
  ],
  emits: ["ringReseted"],
  data() {
    return {
      showResetPopup: false,
    };
  },
  watch: {
    firstStepData() {
      if (sessionStorage.getItem("testTimer") == null) {
        sessionStorage.setItem("testTimer", new Date().getTime());
      }
      if (localStorage.getItem("testTimer") == null) {
        localStorage.setItem("testTimer", new Date().getTime());
      }
    },
  },
  mounted() {
    if (sessionStorage.getItem("testTimer") == null) {
      sessionStorage.setItem("testTimer", new Date().getTime());
    }
    if (localStorage.getItem("testTimer") == null) {
      localStorage.setItem("testTimer", new Date().getTime());
    }
  },
  computed: {
    secDiamondSvg() {
      return 2 <= this.$props.totalSteps ? "white" : "#04043F";
    },
    secSettingSvg() {
      return 2 <= this.$props.totalSteps ? "white" : "#04043F";
    },
    thirdCompleteSvg() {
      return 3 <= this.$props.totalSteps ? "white" : "#04043F";
    },
    // Single source of truth for the "Ring" step's link, so every href pointing
    // at final-ring (hover preview, copy-link, middle-click-open-in-new-tab)
    // matches exactly what the actual navigation (viewPage()) produces.
    finalRingHref() {
      const thirdStepData = this.$props.thirdStepData;
      return buildFinalRingUrl(
        {
          settingId: thirdStepData?.settingId,
          diamondId: thirdStepData?.diamondId,
          diamondType: thirdStepData?.diamondType,
        },
        this.$route?.query
      );
    },
  },
  delimiters: ["[[", "]]"],
  template: `#pc-header-steps`,
  methods: {
    confirmReset(action) {
      this.showResetPopup = false;
      if (action == "yes") {
        this.resetSteps();
      }
    },
    resetSteps() {
      var firstPage = sessionStorage.getItem("firstPage");
      resetRingFromSession();

      if (firstPage == "diamondPage") {
        window.location.href = "/collections/lab-diamonds";
      } else {
        window.location.href = "/collections/engagement-rings";
      }
    },
    setPlaceholderImg(e) {
      var placeholder = e.target.getAttribute("placeholder");
      var pageType = e.target.getAttribute("page-type");
      if (pageType == "diamond") {
        var imgUrl = `${window.location.origin}/cdn/shop/t/9/assets/${placeholder}.png`;
        e.target.style.background = "white";
      } else if (pageType == "setting") {
        var imgUrl = placeholder;
      }
      e.target.setAttribute("src", imgUrl);
    },
    viewFinalRing(e) {
      const thirdStepData = this.$props.thirdStepData;
      var href = buildFinalRingUrl(
        {
          settingId: thirdStepData?.settingId,
          diamondId: thirdStepData?.diamondId,
          diamondType: thirdStepData?.diamondType,
        },
        this.$route?.query
      );
      window.location.href = href ? `${baseUrl}${href}` : `${baseUrl}/collections/final-ring`;
    },
    changeListing(e) {
      var el = e.currentTarget || e.target;
      var type = el.getAttribute("page-type");
      var id = type == "ring" ? "" : el.getAttribute("page-id");
      if (type == "setting") {
        window.location.href = "/collections/engagement-rings";
      } else if (type == "diamond") {
        var diamondTab = el.getAttribute("diamond-type");
        window.location.href = `/${diamondTab}`;
      } else if (type == "ring") {
        var settingId = el.getAttribute("setting-id");
        var diamondId = el.getAttribute("diamond-id");
        var diamondType = el.getAttribute("diamond-type");
        var href = buildFinalRingUrl({ settingId, diamondId, diamondType }, this.$route?.query);
        if (href) window.location.href = `${baseUrl}${href}`;
      }
    },
    generateHref() {
      if ($t(this.firstStepData.headerTitle) === "DIAMOND") {
        return "/collections/natural-diamonds";
      } else if ($t(this.firstStepData.headerTitle) === "SETTING") {
        return "/collections/engagement-rings";
      } else {
        return "";
      }
    },
    viewPage(e) {
      var el = e.currentTarget || e.target;
      var type = el.getAttribute("page-type");
      var handle = el.getAttribute("handle");
      var id = type == "ring" ? "" : el.getAttribute("page-id");
      if (type == "setting") {
        let obj = safeParseJSON("selectedSetting");
        if (!obj) return;
        var settingHandle = handle || (obj && obj.handle ? obj.handle : '');
        window.location.href = `/collections/engagement-rings/products/${settingHandle}?variant=${obj.id}`;
      } else if (type == "diamond") {
        let obj = safeParseJSON("selectedDiamond");
        if (!obj) return;
        const diamondT = this.getDiamondCollectionPath(obj.diamondType);
        window.location.href = `/collections/${diamondT}/diamonds?diamondId=${obj.diamondId}`;
      } else if (type == "ring") {
        var settingId = el.getAttribute("setting-id");
        var diamondId = el.getAttribute("diamond-id");
        var diamondType = el.getAttribute("diamond-type");
        var href = buildFinalRingUrl({ settingId, diamondId, diamondType }, this.$route?.query);
        if (href) window.location.href = `${baseUrl}${href}`;
      }
    },
    _resetStepData(headerTitle) {
      let sessionStorageData = safeParseJSON("stepData", {});
      let removedKey = "";
      Object.keys(sessionStorageData).forEach((v) => {
        if (sessionStorageData[v]["headerTitle"] == headerTitle) {
          removedKey = v;
          sessionStorageData[v] = { headerTitle: headerTitle };
        }
      });
      sessionStorageData["thirdStep"] = { headerTitle: "Ring" };

      /* If firstStep was removed and secondStep has data, promote secondStep to firstStep and flip firstPage */
      if (removedKey == "firstStep" && sessionStorageData.secondStep && sessionStorageData.secondStep.title) {
        sessionStorageData.firstStep = sessionStorageData.secondStep;
        sessionStorageData.secondStep = { headerTitle: headerTitle };
        /* flip firstPage so step order matches the remaining item */
        var currentFirstPage = sessionStorage.getItem("firstPage");
        sessionStorage.setItem("firstPage", currentFirstPage == "diamondPage" ? "settingPage" : "diamondPage");
      }
      /* If secondStep was removed and firstStep has data, no swap needed — firstStep stays as is */

      sessionStorage.setItem("stepData", JSON.stringify(sessionStorageData));
    },
    _removeStickyItem(pageType) {
      let getSticky = safeParseJSON("stickyBarItems", []);
      if (getSticky.length) {
        let index = getSticky.findIndex((v) => v.page == pageType);
        if (index !== -1) {
          getSticky.splice(index, 1);
        }
        sessionStorage.setItem("stickyBarItems", JSON.stringify(getSticky));
      }
    },
    getDiamondCollectionPath(diamondType) {
      if (!diamondType) return "natural-diamonds";
      return diamondType == "white" ? "natural-diamonds"
        : diamondType == "lab" ? "lab-diamonds"
        : diamondType == "white_color" ? "natural-fancy-coloured-diamonds"
        : diamondType == "lab_color" ? "coloured-lab-grown-diamonds"
        : "natural-diamonds";
    },
    removeListing(e) {
      var el = e.currentTarget || e.target;
      var type = el.getAttribute("page-type");
      var firstPage = sessionStorage.getItem("firstPage") || "";

      if (type == "setting") {
        this._resetStepData("SETTING");
        this._removeStickyItem("setting");

        sessionStorage.removeItem("selectedSecondDiamond");
        sessionStorage.removeItem("searchingSecondDiamond");
        sessionStorage.removeItem("selectedSetting");
        sessionStorage.removeItem("selectedMatchingBand");
        sessionStorage.removeItem("selectedSettingOptions");

        if (this.$route.name == "chooseSetting") {
          window.location.reload();
        } else {
          window.location.href = "/collections/engagement-rings";
          if (this.$refs.mainAnchor) {
            this.$refs.mainAnchor.href = "/collections/engagement-rings";
          }
        }
        return;
      } else if (type == "diamond") {
        var diamondType =
          getDiamondListingType(el.getAttribute("diamond-type")) ||
          el.getAttribute("diamond-type");

        this._resetStepData("DIAMOND");
        this._removeStickyItem("diamond");

        // Read diamond data before removing — needed unconditionally now (not just for
        // settingPage) to sync the actual Shopify cart below.
        let selectedDiamond = safeParseJSON("selectedDiamond");

        sessionStorage.removeItem("selectedDiamond");
        sessionStorage.removeItem("searchingSecondDiamond");

        // On the final-ring page, don't blow away the page the shopper is already on
        // with a full reload/redirect — the step-bar/sticky-bar are already updated
        // above (in sessionStorage); let that page sync itself and show its own
        // "diamond removed" popup instead of navigating away.
        if (this.$route.name == "finalRingPage") {
          // This diamond may already be sitting in the real Shopify cart (e.g. the
          // shopper clicked "Add to Cart"/"Complete Purchase" earlier, then came back
          // and removed it here) — session cleanup alone doesn't touch the cart, so
          // sync it in the background. Re-check gift eligibility unconditionally
          // afterward — NOT only when a matching diamond line was actually found —
          // since the gift depends on the cart's overall total, which may already be
          // stale/ineligible regardless of whether this specific diamond was in it.
          fetch("/cart.js")
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var diamondLine = (selectedDiamond && selectedDiamond.diamondId)
                ? (cart.items || []).find(function (item) {
                    var sku = item.sku || (item.properties && item.properties.sku);
                    return String(sku) === String(selectedDiamond.diamondId);
                  })
                : null;
              var removeDiamond = diamondLine
                ? fetch("/cart/change.js", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: diamondLine.key, quantity: 0 }),
                  })
                : Promise.resolve();
              return removeDiamond.then(function () {
                return fetch("/cart.js").then(function (r) { return r.json(); });
              });
            })
            .then(function (cartAfter) {
              var GIFT_VARIANT_3500 = 67834200031596;
              var GIFT_VARIANT_5000 = 52378702283116;
              var giftLine = cartAfter.items.find(function (i) {
                return i.variant_id === GIFT_VARIANT_3500 || i.variant_id === GIFT_VARIANT_5000;
              });
              if (!giftLine) return;
              // Conservative (non-discount-adjusted) total — only ever equal to or
              // higher than the real discount-aware total the cart page computes, so
              // if THIS says the threshold isn't met, it definitely isn't. The cart
              // page's own accurate check corrects anything this misses.
              var approxTotal = cartAfter.items.reduce(function (sum, i) {
                if (i.properties && i.properties._gift) return sum;
                if (i.product_type === "Appraisal") return sum;
                return sum + i.line_price;
              }, 0);
              if (approxTotal < 350000) {
                fetch("/cart/change.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ id: giftLine.key, quantity: 0 }),
                });
              }
            })
            .catch(function (e) {
              console.error("[ROA] Failed to sync cart after diamond removal:", e);
            });
          window.dispatchEvent(
            new CustomEvent("roa:diamond-removed", { detail: { diamondType: diamondType } })
          );
          return;
        }

        if (
          this.$route.name == "diamondType" ||
          this.$route.name == "diamondsbyshape"
        ) {
          window.location.reload();
        } else {
          var targetUrl;
          if (firstPage == "settingPage" && selectedDiamond && selectedDiamond.diamondType) {
            targetUrl = "/" + this.getDiamondCollectionPath(selectedDiamond.diamondType);
          } else {
            targetUrl = "/collections/" + (diamondType || "natural-diamonds");
          }
          window.location.href = targetUrl;
          if (this.$refs.mainAnchor) {
            this.$refs.mainAnchor.href = targetUrl;
          }
        }
        return;
      }
    },
  },
};

/* get diamond listing type to load */
const getDiamondListingType = (diamondType) => {
  switch (diamondType) {
    case "white":
      return "natural-diamonds";
    case "lab":
      return "lab-diamonds";
    case "white_color":
      return "natural-fancy-coloured-diamonds";
    case "lab_color":
      return "coloured-lab-grown-diamonds";
    case "fancy":
      return "fancy-diamonds";
    case "gemstone":
      return "gemstone-diamonds";
  }
};

/* Vue spinner */
const SpinnerComp = {
  props: ["listViewType"],
  data() {
    return {
      fakes: 20,
    };
  },
  delimiters: ["[[", "]]"],
  template: `#vue-spinner`,
};

/* Detail page spinner */
const DetailSpinnerComp = {
  data() {
    return {
      fakes: 20,
    };
  },
  delimiters: ["[[", "]]"],
  template: `#detail-vue-spinner`,
};

/* data not found component */
const DataNotFoundComp = {
  props: ["activeTab", "diamondType"],
  emits: ["resetFilters"],
  delimiters: ["[[", "]]"],
  template: `#data-not-found`,
};

/* Grid Tabs Component */
const ListingGridTabs = {
  props: [
    "filters",
    "compareDiamonds",
    "diamondCount",
    "ResultView",
    "hideCompareDiamondsTab",
    "hideResultViewSwitch",
    "headerLabel",
    "recentDiamondsCount",
  ],
  emits: ["shipDiamond", "changeResultView"],
  setup(props, context) {
    const currentGridTab = inject("currentGridTab");
    const diamondCompareState = inject("diamondsForCompare");
    return {
      ...toRefs(diamondCompareState),
      ...toRefs(currentGridTab),
    };
  },
  delimiters: ["[[", "]]"],
  template: `#listing-grid-tabs`,
};

/* Reset Filters Popup component */
const ResetFormComp = {
  props: ["isShowResetForm"],
  emits: ["hideResetForm", "resetParentFilters"],
  delimiters: ["[[", "]]"],
  template: `#reset-form`,
  methods: {
    handleResetFilters(e) {
      this.$emit("hideResetForm");
      this.$emit("resetParentFilters");
    },
  },
};

/* diamond detail popup on filter page */
const DiamondDetailPopupComp = {
  props: ["diamondData"],
  setup(props) {
    const diamondData = inject("diamondDataState");
    function closeDetailPopup(e) {
      e.stopPropagation();
      diamondData.diamondId = "";
    }
    return {
      closeDetailPopup,
      ...toRefs(diamondData),
    };
  },
  delimiters: ["[[", "]]"],
  template: `#diamond-detail-popup`,
  methods: {
    getPageTitle(diamondData) {
      return getDiamondTitle(diamondData);
    },
  },
};

/* tooltip component for detail page */
const TooltipComp = {
  props: ["showTooltip", "wishlistText", "tooltipClass"],
  delimiters: ["[[", "]]"],
  template: `#detail-tooltip`,
};

/* compact attribute tooltip — info icon with hover/focus bubble */
const AttrTipComp = {
  props: {
    text: { type: String, required: true },
    placement: { type: String, default: "top" }, // top | bottom
  },
  delimiters: ["[[", "]]"],
  template: `
    <span class="nrl-attr-tip" :class="'nrl-attr-tip--' + placement" tabindex="0" @click.stop>
      <svg class="nrl-attr-tip-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="16" x2="12" y2="12"/>
        <line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
      <span class="nrl-attr-tip-bubble">[[ text ]]</span>
    </span>
  `,
};

/* diamond with skin comparer */
const diamondSkinComparer = {
  components: {
    slider: VueformSlider,
  },
  props: ["currentCaratWeight"],
  data() {
    return {
      skinSlider: {
        initialOpacity: 0.5,
        minOpacity: 0,
        maxOpacity: 1,
        step: -1,
      },
      caratSlider: {
        initialCarat: this.$props.currentCaratWeight,
        minCarat: 0.2,
        maxCarat: 10,
        step: -1,
      },
      darkHandImg: `${baseAssetsUrl}/Loose-Diamond-Hand-Dark.jpg`,
      whiteHandImg: `${baseAssetsUrl}/Loose-Diamond-Hand-White.jpg`,
    };
  },
  delimiters: ["[[", "]]"],
  template: `#diamond-skin-comparer`,
  computed: {
    format() {
      return function (value) {
        return `${value.toFixed(2)} CT`;
      };
    },
    getDiamondImgSize() {
      if (
        this.caratSlider.initialCarat >= 0.2 &&
        this.caratSlider.initialCarat <= 5.02
      ) {
        return `${(this.caratSlider.initialCarat / 100) * 100 + 23}%`;
      } else {
        return `${(this.caratSlider.initialCarat / 100) * 100 + 31.5}%`;
      }
    },
  },
};

/* GIA certificate popup */
const CertificateImgPopup = {
  props: ["certImgUrl", "showPopup", "showLoader"],
  emits: ["closeCertPopup", "hideCertImgLoader"],
  delimiters: ["[[", "]]"],
  template: `#gia-certificate-popup`,
  methods: {
    closePopup(e) {
      this.$emit("closeCertPopup");
    },
    hideLoader(e) {
      this.$emit("hideCertImgLoader");
    },
  },
};

/* main image popup component */
const FullImgPopup = {
  data() {
    return {
      imgUrl: "",
    };
  },
  emits: ["closeImgPopup"],
  delimiters: ["[[", "]]"],
  template: `#img-popup-div`,
  methods: {
    setImageUrl(imgUrl) {
      this.imgUrl = imgUrl;
    },
    closePopup() {
      this.imgUrl = "";
      this.$emit("closeImgPopup");
    },
  },
};

/* diamond scalar image comp */
const DiamondCanvasComp = {
  delimiters: ["[[", "]]"],
  template: `#diamond-canvas-comp`,
  props: ["canvasImgType", "diamondData"],
  emits: ["hideLoader"],
  data() { },
  mounted() {
    if (this.$props.canvasImgType == "front") {
      this.drawDiamondFrontImgOnCanvas(this.$props.diamondData);
    } else {
      this.drawDiamondSideImgOnCanvas(this.$props.diamondData);
    }
  },
  methods: {
    drawDiamondFrontImgOnCanvas(diamond) {
      var diamondLength = `${diamond.length} mm`;
      var diamondWidth = `${diamond.width} mm`;
      var imgParams = this.getDiamondImgParams(diamond.shape.toLowerCase());
      var canvasEl = this.$refs.diamond_canvas;
      var context = canvasEl.getContext("2d");
      context.fillStyle = "rgb(0,0,0,1)";
      context.beginPath();
      var img = new Image();
      img.src = `${baseAssetsUrl}/${diamond.shape.toLowerCase()}_front.png`;
      img.onload = () => {
        this.$emit("hideLoader");
        context.drawImage(img, 0, 0);
        context.font = "11px/bold open sans, arial";
        context.fillText(
          diamondWidth,
          imgParams.width_width,
          imgParams.width_height
        );
        context.fillText(
          diamondLength,
          imgParams.length_width,
          imgParams.length_height
        );
      };
    },
    drawDiamondSideImgOnCanvas(diamond) {
      var diamondTablePerc = diamond.tablePerc;
      var diamondDepthPerc = diamond.deptPerc;
      var diamondGridle =
        !diamond.girdleMin || !diamond.girdleMax
          ? "None"
          : `${diamond.girdleMin}-${diamond.girdleMax}`;
      var imgParams = this.getDiamondImgParams(diamond.shape.toLowerCase());
      var canvasEl = this.$refs.diamond_canvas;
      var context = canvasEl.getContext("2d");
      context.fillStyle = "rgb(0,0,0,1)";
      context.beginPath();
      var img = new Image();
      img.src = `${baseAssetsUrl}/${diamond.shape.toLowerCase()}_side.png`;
      img.onload = () => {
        this.$emit("hideLoader");
        context.drawImage(img, 0, 0);
        context.font = "11px/bold open sans, arial";
        context.fillText(
          diamondTablePerc,
          imgParams.table_width,
          imgParams.table_height
        );
        context.fillText(
          diamondDepthPerc,
          imgParams.depth_width,
          imgParams.depth_height
        );
        context.fillText(
          diamondGridle,
          imgParams.girdle_width,
          imgParams.girdle_height
        );
      };
    },
    getDiamondImgParams(diamondShape) {
      var imgParams = {};
      switch (diamondShape) {
        case "round":
          imgParams["width_width"] = 195;
          imgParams["width_height"] = 17;
          imgParams["length_width"] = 65;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 195;
          imgParams["table_height"] = 38;
          imgParams["depth_width"] = 310;
          imgParams["depth_height"] = 130;
          imgParams["girdle_width"] = 101;
          imgParams["girdle_height"] = 210;
          break;

        case "princess":
          imgParams["width_width"] = 185;
          imgParams["width_height"] = 15;
          imgParams["length_width"] = 45;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 200;
          imgParams["table_height"] = 35;
          imgParams["depth_width"] = 315;
          imgParams["depth_height"] = 147;
          imgParams["girdle_width"] = 112;
          imgParams["girdle_height"] = 210;
          break;

        case "cushion":
          imgParams["width_width"] = 190;
          imgParams["width_height"] = 19;
          imgParams["length_width"] = 50;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 200;
          imgParams["table_height"] = 32;
          imgParams["depth_width"] = 290;
          imgParams["depth_height"] = 155;
          imgParams["girdle_width"] = 160;
          imgParams["girdle_height"] = 200;
          break;

        case "radiant":
          imgParams["width_width"] = 190;
          imgParams["width_height"] = 14;
          imgParams["length_width"] = 48;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 200;
          imgParams["table_height"] = 34;
          imgParams["depth_width"] = 310;
          imgParams["depth_height"] = 147;
          imgParams["girdle_width"] = 165;
          imgParams["girdle_height"] = 200;
          break;

        case "asscher":
          imgParams["width_width"] = 180;
          imgParams["width_height"] = 15;
          imgParams["length_width"] = 42;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 200;
          imgParams["table_height"] = 35;
          imgParams["depth_width"] = 315;
          imgParams["depth_height"] = 148;
          imgParams["girdle_width"] = 112;
          imgParams["girdle_height"] = 210;
          break;

        case "emerald":
          imgParams["width_width"] = 190;
          imgParams["width_height"] = 12;
          imgParams["length_width"] = 60;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 200;
          imgParams["table_height"] = 40;
          imgParams["depth_width"] = 300;
          imgParams["depth_height"] = 145;
          imgParams["girdle_width"] = 110;
          imgParams["girdle_height"] = 200;
          break;

        case "oval":
          imgParams["width_width"] = 200;
          imgParams["width_height"] = 17;
          imgParams["length_width"] = 80;
          imgParams["length_height"] = 150;

          imgParams["table_width"] = 200;
          imgParams["table_height"] = 47;
          imgParams["depth_width"] = 305;
          imgParams["depth_height"] = 140;
          imgParams["girdle_width"] = 112;
          imgParams["girdle_height"] = 200;
          break;

        case "pear":
          imgParams["width_width"] = 195;
          imgParams["width_height"] = 15;
          imgParams["length_width"] = 85;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 210;
          imgParams["table_height"] = 45;
          imgParams["depth_width"] = 310;
          imgParams["depth_height"] = 140;
          imgParams["girdle_width"] = 124;
          imgParams["girdle_height"] = 200;
          break;

        case "marquise":
          imgParams["width_width"] = 192;
          imgParams["width_height"] = 17;
          imgParams["length_width"] = 65;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 200;
          imgParams["table_height"] = 47;
          imgParams["depth_width"] = 305;
          imgParams["depth_height"] = 140;
          imgParams["girdle_width"] = 80;
          imgParams["girdle_height"] = 200;
          break;

        case "heart":
          imgParams["width_width"] = 185;
          imgParams["width_height"] = 18;
          imgParams["length_width"] = 60;
          imgParams["length_height"] = 145;

          imgParams["table_width"] = 210;
          imgParams["table_height"] = 32;
          imgParams["depth_width"] = 290;
          imgParams["depth_height"] = 155;
          imgParams["girdle_width"] = 132;
          imgParams["girdle_height"] = 215;
          break;
      }
      return imgParams;
    },
  },
};

/* diamond media gallery */
const diamondMediaGallery = {
  components: {
    CertificateImgPopup,
    FullImgPopup,
    DiamondCanvasComp,
  },
  data() {
    return {
      gallery: {
        type: "image",
        pos: 1,
      },
      GIAImage: `${baseAssetsUrl}/gia.png`,
      VideoImage: `${baseAssetsUrl}/360video.jpg`,
      darkHandImg: `${baseAssetsUrl}/Loose-Diamond-Hand-Dark.jpg`,
      whiteHandImg: `${baseAssetsUrl}/Loose-Diamond-Hand-White.jpg`,
      showIframeLoader: false,
      showCertificateImgPopup: false,
      certificateImgUrl: "",
      showCertImgLoader: false,
      zoomImgUrl: "",
      showZoomImgIcon: true,
      isMobileDevice: false,
      showPopupImg: false,
      diamondLab: "",
      coupon_ring_img: `${baseAssetsUrl}/bangles.png`,
      coupon_bangle_img: `${baseAssetsUrl}/bangles.png`,
      loadingGif: `${baseAssetsUrl}/loading_small.gif`,
      srcSet: false,
      videoNotAvail: false,
      currentCaratWeight: 0,
      currentShape: "",
      basePxPerMM: 15, // Base scaling factor
      currentHand: 0,
      lightboxOpen: false,
    };
  },
  watch: {
    "$route.params.diamondId": {
      handler(diamondId) {
        if (diamondId != undefined) {
          this.gallery.type = "image";
          this.gallery.pos = 1;
        }
      },
    },
    diamond: {
      handler(diamond) {
        if (diamond != undefined) {
          this.currentCaratWeight = diamond.caratWeight;
          this.currentShape = `https://cdn.shopify.com/s/files/1/0900/1870/9868/files/${diamond.shape ? diamond.shape.toLowerCase() : "round"
            }.png`;
        }
      },
    },
  },
  props: {
    diamond: {
      type: Object,
      required: true,
    },
    hands: {
      type: String,
      default: "",
    },
  },
  emits: ["imageError"],
  delimiters: ["[[", "]]"],
  template: `#diamond-media-gallery`,
  mounted() {
    window.addEventListener("resize", this.updateDiamondSize);
    window.addEventListener("keydown", this._dmLbKeydown);

    this.diamondLab = this.$props.diamond.lab;
    if (window.innerWidth <= 767) {
      this.showZoomImgIcon = false;
      this.isMobileDevice = true;
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateDiamondSize);
    window.removeEventListener("keydown", this._dmLbKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateDiamondSize);
    window.removeEventListener("keydown", this._dmLbKeydown);
  },
  computed: {
   diamondSize() {
      const carat = this.currentCaratWeight || 1;

      // Clamp between 0.1 and 7 carats
      const clampedCarat = Math.min(Math.max(carat, 0.1), 7);

      // Scale linearly: min scale at 0.1 carat, max scale at 7 carats
      const minScale = 0.2;
      const maxScale = 0.75;
      const scale =
        minScale + ((clampedCarat - 0.1) / (7 - 0.1)) * (maxScale - minScale);

      const vw = window.innerWidth;

      let baseSize;

      if (vw <= 480) {
        // Small mobile
        baseSize = Math.min(vw * 0.055, 60);
      } else if (vw <= 768) {
        // Tablet / large mobile
        baseSize = Math.min(vw * 0.045, 70);
      } else if (vw <= 1024) {
        // Small desktop / laptop
        baseSize = Math.min(vw * 0.030, 75);
      } else {
        // Large desktop
        baseSize = Math.min(vw * 0.025, 80);
      }

      return Math.round(baseSize * scale);
    },

    getPlaceholderImage() {
      return `${baseAssetsUrl}/${this.$props.diamond.shape}.png`;
    },
    getCertificatePlaceholderImage() {
      return function (labType) {
        return `${baseAssetsUrl}/${labType.toLowerCase()}.png`;
      };
    },
    showDiamondScalarFrontViewImg() {
      return (diamond) => {
        return diamond.length != "" && diamond.width != "" ? true : false;
      };
    },
    getDiamondScalarFrontViewImg() {
      return (diamond) => {
        if (diamond.shape) {
          return `${baseAssetsUrl}/${diamond.shape.toLowerCase()}_front.png`;
        }
      };
    },
    getHandImg() {
      return (diamond) => {
        if (diamond) {
          return `${diamond}`;
        }
      };
    },
    showDiamondScalarSideViewImg() {
      return (diamond) => {
        return diamond.tablePerc != "" && diamond.deptPerc != "" ? true : false;
      };
    },
    getDiamondScalarSideViewImg() {
      return (diamond) => {
        if (diamond.shape) {
          return `${baseAssetsUrl}/${diamond.shape.toLowerCase()}_side.png`;
        }
      };
    },
  },
  methods: {
    openDiamondLightbox() {
      if (!this.diamond || !this.diamond.diamondImage) return;
      this.lightboxOpen = true;
      document.body.style.overflow = 'hidden';
    },
    closeDiamondLightbox() {
      this.lightboxOpen = false;
      document.body.style.overflow = '';
    },
    _dmLbKeydown(e) {
      if (this.lightboxOpen && e.key === 'Escape') this.closeDiamondLightbox();
    },
    updateDiamondSize() {
      this.$forceUpdate(); // or set a reactive screen width
    },
    handleError(e) {
      debugger;
    },
    openMobileFullImage(imageUrl) {
      if (this.isMobileDevice) {
        this.showPopupImg = true;
        this.$refs.fullImg.setImageUrl(imageUrl);
      }
    },
    handleIframeLoader(e, showLoader, type = undefined) {
      // if(e.type == 'load') {
      // if(e.target.src.includes('v360.diamonds')){
      //     localStorage.setItem('videonoload', JSON.stringify(e))
      // } else {
      //     localStorage.setItem('videoloaded', JSON.stringify(e))

      // }
      //     if(!e.currentTarget.browsingTopics != undefined && e.currentTarget.browsingTopics == false){
      //         this.showIframeLoader = showLoader
      //     } else {
      //         e.srcElement.src = this.loadingGif
      //         this.srcSet = true
      //     }
      // } else {
      // }
      this.showIframeLoader = showLoader;
      if (!!type && type == "videoClick") {
        this.videoNotAvail = false;
      }
      if (e.type == "load" && !showLoader) {
        this.checkSetLink(e);
      }
    },
    checkSetLink(e) {
      // Get a handle to the iframe element
      var iframe = document.querySelector("iframe");

      try {
        var iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        if (iframeDoc.readyState == "complete") {
          //iframe.contentWindow.alert("Hello");
          iframe.contentWindow.onload = function () {
            alert("I am loaded");
          };
          // The loading is complete, call the function we want executed once the iframe is loaded
          // this.afterLoading();
          this.videoNotAvail = true;
          return;
        }
      } catch {
        console.error("no error");
        this.srcSet = true;
        return;
      }

      // Check if loading is complete

      // If we are here, it is not loaded. Set things up so we check   the status again in 100 milliseconds
    },
    afterLoading() {
      alert("I am here");
    },
    setMainMedia(e, mediaType, mediaPos = 1) {
      if (mediaType === "cert") {
        this.gallery.type = "image";
        this.gallery.pos = 1;
        var certificateFileUrl = e.currentTarget.attributes["cert_url"].value;
        var fileExtension = certificateFileUrl.split(".").pop().toLowerCase();
        var allowedExtension = ["jpg", "jpeg", "jfif", "pjpeg", "pjp"];
        if (!allowedExtension.includes(fileExtension)) {
          this.certificateUrl = certificateFileUrl;
          window.open(
            this.certificateUrl,
            "",
            "left=550,top=300,width=700,height=500"
          );
        } else {
          this.showCertificateImgPopup = true;
          this.certificateImgUrl = certificateFileUrl;
          this.showCertImgLoader = true;
        }
      } else if (mediaType === "cert_num") {
        this.gallery.type = "image";
        this.gallery.pos = 1;
        var certificateNumber = e.currentTarget.attributes["cert_number"].value;
        var labType = e.currentTarget.attributes["lab_type"].value;
        if (labType.toLowerCase() == "gia") {
          this.certificateUrl = `https://www.gia.edu/report-check?reportno=${certificateNumber}`;
        } else if (labType.toLowerCase() == "igi") {
          this.certificateUrl = `https://www.igi.org/viewpdf.php?r=${certificateNumber}`;
        } else if (labType.toLowerCase() == "gcal") {
          this.certificateUrl = `https://www.gcalusa.com/certificate-search.html?certificate_id=${certificateNumber}`;
        } else if (labType.toLowerCase() == "hrd") {
          this.certificateUrl = `https://my.hrdantwerp.com/Download/GetGradingReportPdf/?reportNumber=${certificateNumber}`;
        } else if (labType.toLowerCase() == "ags") {
          this.certificateUrl = `https://agslab.com/ym-vdgr/en-us/diamonds/${certificateNumber}/grading-report`;
        }
        window.open(
          this.certificateUrl,
          "",
          "left=550,top=300,width=700,height=500"
        );
      } else {
        this.gallery.type = mediaType;
        this.gallery.pos = mediaPos;
      }
    },
    showFullImgPopup() {
      this.showPopupImg = true;
      this.$refs.fullImg.setImageUrl(this.zoomImgUrl);
    },
    getDiamondSrc(diamond) {
      if (!diamond || !diamond.diamondImage) return getShapeFallbackImage(diamond && diamond.shape);
      return diamond.diamondImage;
    },
    handleDiamondImgError(e, shape) {
      var target = e.target || e.currentTarget;
      var fallback = getShapeFallbackImage(shape);
      target.removeAttribute("srcset");
      if (target.src === shapeToImageUrl.DEFAULT) return;
      if (target.src === fallback) {
        target.src = shapeToImageUrl.DEFAULT;
      } else {
        target.src = fallback;
      }
    },
  },
};

/* diamond hint popup */
const DiamondHintPopupComp = {
  data() {
    return {
      errorCount: 0,
      hint: {
        recipientEmail: "",
        recipientName: "",
        senderEmail: "",
        msg: "",
        acceptTerms: false,
        success: 0,
      },
      hintError: {
        recipientEmail: 0,
        recipientEmailValid: 0,
        recipientName: 0,
        senderEmail: 0,
        senderEmailValid: 0,
        acceptTerms: 0,
      },
      detailLogo: `${baseAssetsUrl}/logo/detail_logo.svg`,
      shortLogo: `${baseAssetsUrl}/logo/logo.svg`,
      productUrl: "",
      errorList: {},
      emailSent: false,
      receiver_name: "",
      checkSrc: `${baseAssetsUrl}/check.gif`,
      secondImage: "",
    };
  },
  props: [
    "type",
    "openPopup",
    "diamondData",
    "settingData",
    "popupTitle",
    "popupImage",
    "popupPlaceholderImage",
  ],
  emits: ["closeHintPopup", "imageError"],
  delimiters: ["[[", "]]"],
  template: `#diamond-hint-popup`,
  computed: {
    urlOfProduct() {
      if (this.$props.type == "final") {
        return `https://ringsofaustralia.com.au${window.location.pathname}`;
      } else {
        return `https://ringsofaustralia.com.au${window.location.pathname}`;
      }
    },
    diamondLabel() {
      if (this.$props.type == "diamond") {
        let label = `${this.$props.diamondData.caratWeight} Carat ${this.$props.diamondData.shape} Diamond`;
        return label;
      } else if (this.$props.type == "setting") {
        return this.$props.diamondData.name;
      } else if (this.$props.type == "final") {
        return `${this.$props.settingData.name} With ${this.$props.diamondData.caratWeight
          } Carat ${this.$props.diamondData.shape} ${this.$props.diamondData.diamondType == "white"
            ? "Natural"
            : this.$props.diamondData.diamondType == "white_color"
              ? "Natural Colour"
              : this.$props.diamondData.diamondType == "lab_color"
                ? "Lab Grown"
                : this.$props.diamondData.diamondType == "lab"
                  ? "Lab Grown"
                  : ""
          } Diamond`;
      }
    },
    image() {
      if (this.$props.type == "diamond") {
        let label = this.$props.diamondData.diamondImage;
        return label;
      } else if (this.$props.type == "setting") {
        return this.$props.diamondData.main_image;
      } else if (this.$props.type == "final") {
        this.secondImage = this.$props.diamondData.diamondImage;
        return `${this.$props.settingData.main_image}`;
      }
    },
  },
  watch: {
    openPopup() {
      if (this.$props.openPopup) {
        setTimeout(() => {
          if (!!localStorage.getItem("hint-form-test")) {
            jQuery(".pc-receiver-name").val("abcdef");
            jQuery(".pc-sender-name").val("uvwxyz");
            jQuery(".pc-sender-email").val("xyz@gmail.com");
          }
        }, 800);
      }
    },
  },
  methods: {
    setShapeImage(e) {
      var target = e.currentTarget;
      var stage = parseInt(target.dataset.imgErrorStage || "0");
      target.dataset.imgErrorStage = String(stage + 1);
      if (stage === 0) {
        target.style.background = "#ddd";
        target.setAttribute("src", getShapeFallbackImage(this.$props.diamondData.shape));
      } else if (stage === 1) {
        target.setAttribute("src", shapeToImageUrl.DEFAULT);
      }
    },
    closePopup(e) {
      this.$emit("closeHintPopup");
      jQuery(".pc-receiver-name").val("");
      jQuery(".pc-receiver-email").val("");
      jQuery(".pc-sender-name").val("");
      jQuery(".pc-sender-email").val("");
      jQuery(".pc-product-url").val("");
      jQuery(".pc-product-name").val("");
      // jQuery('.pc-product-image').val('');
      jQuery(".pc-custom-message").val("");
      document.querySelector(".pc-send-btn").removeAttribute("disabled", true);
      document.querySelector(".pc-send-btn").style.opacity = 1;
      document.querySelector(".pc-send-btn").style.cursor = "pointer";
      document.querySelector(".pc-hint-submit-loading").style.display = "none";
      // document.querySelector('.pc-send-hint-form').style.display = 'flex'
      // document.querySelector('.pc-hint-header').style.display = 'flex'
      // document.querySelector('.thank-you-message').style.display = 'none'
    },
    validation(field) {
      let valid = true;
      if (field.receiver_name == "") {
        this.errorList["receiver_name"] = true;
        valid = false;
      }
      if (field.your_name == "") {
        this.errorList["your_name"] = true;
        valid = false;
      }
      if (
        !!!field.your_email.match(
          "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$"
        )
      ) {
        this.errorList["your_email"] = true;
        valid = false;
      }
      if (
        !!!field.receiver_email.match(
          "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$"
        )
      ) {
        this.errorList["receiver_email"] = true;
        valid = false;
      }
      return valid;
    },
    popOutField(field) {
      // let index = this.errorList.indexOf(field)
      // let arr = this.errorList.slice(0)
      // arr.splice(index,1)
      delete this.errorList[field];
    },
    submitForm() {
      var receiver_name = jQuery(".pc-receiver-name").val();
      receiver_email = jQuery(".pc-receiver-email").val();
      your_name = jQuery(".pc-sender-name").val();
      your_email = jQuery(".pc-sender-email").val();
      product_url = jQuery(".pc-product-url").val();
      product_name = jQuery(".pc-product-name").val();
      product_image = document
        .querySelector(".pc-hint-image img")
        .getAttribute("src");
      custom_message = jQuery(".pc-custom-message").val();

      // debugger
      if (
        this.validation({
          receiver_name,
          receiver_email,
          your_name,
          your_email,
        })
      ) {
        document.querySelector(".pc-hint-submit-loading").style.display =
          "flex";
        document.querySelector(".pc-send-btn").style.cursor = "wait";
        document.querySelector(".pc-send-btn").setAttribute("disabled", true);
        document.querySelector(".pc-send-btn").style.opacity = 0.8;
        document.querySelector(".pc-hint-submit-loading").style.display = "none";
        document.querySelector(".pc-send-hint-form").style.display = "none";
        document.querySelector(".pc-hint-header").style.display = "none";
        document.querySelector(".thank-you-message").style.display = "flex";
        document.querySelector(".thank-you-message .pc-message").innerText = `Your hint has been sent to ${receiver_name}.`;
      }
    },
    /* submitForm(e) {            
            for(formField in this.hint) {
                if (formField === 'msg' || formField == 'success') continue
                if (formField === 'recipientEmail' || formField === 'senderEmail') {
                    if(this.hint[formField] == '' || this.hint[formField] == null || this.hint[formField] == undefined) {
                        this.hintError[formField + 'Valid'] = 0
                        this.hintError[formField] = 1                
                    } else if (!this.validateEmail(this.hint[formField])) {
                        this.hintError[formField + 'Valid'] = 1
                        this.hintError[formField] = 0                
                    } else {
                        this.hintError[formField + 'Valid'] = 0
                        this.hintError[formField] = 0                
                    }
                } else if (formField == 'acceptTerms') {
                    if (!this.hint[formField]) {
                        this.hintError[formField] = 1
                    } else {
                        this.hintError[formField] = 0
                    }
                } else {
                    if(this.hint[formField] == '' || this.hint[formField] == null || this.hint[formField] == undefined) {
                        this.hintError[formField] = 1                
                    } else {
                        this.hintError[formField] = 0                
                    }
                }
            }

            for(var errorField in this.hintError) {
                if (this.hintError[errorField] === 1) {
                    this.errorCount = 1
                    return
                }
            }
            this.errorCount = 0            
            if(!this.errorCount) {                
                this.hint.success = 1
                setTimeout(() => {
                    this.$emit('closeHintPopup')
                }, 3000)
                //action perform to submit drop a hint form                
            }
        }, */
    validateEmail(emailId) {
      const emailValidator = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      return emailValidator.test(emailId);
    },
  },
};

/* diamond email popup */
const DiamondEmailPopupComp = {
  data() {
    return {
      hideEmailForm: false,
      showThankyouDiv: false,
      errorCount: 0,
      btnText: "Submit",
      email: {
        name: "",
        email: "",
        phone: "",
        msg: "",
        success: 0,
      },
      emailError: {
        name: 0,
        email: 0,
        emailValid: 0,
        phone: 0,
        phoneValid: 0,
      },
    };
  },
  props: [
    "openPopup",
    "diamondData",
    "popupTitle",
    "popupImage",
    "popupPlaceholderImage",
    "formType",
  ],
  emits: ["closeEmailPopup", "imageError"],
  delimiters: ["[[", "]]"],
  template: `#diamond-email-popup`,
  methods: {
    closePopup(e) {
      for (var field in this.email) {
        if (field == "success") {
          this.email[field] = 0;
        } else {
          this.email[field] = "";
        }
      }
      for (var field in this.emailError) {
        this.emailError[field] = 0;
      }
      this.hideEmailForm = false;
      this.showThankyouDiv = false;
      this.$emit("closeEmailPopup");
    },
    submitForm(e) {
      for (formField in this.email) {
        if (formField === "msg" || formField == "success") continue;
        if (formField === "email") {
          if (
            this.email.email == "" ||
            this.email.email == null ||
            this.email.email == undefined
          ) {
            this.emailError.emailValid = 0;
            this.emailError.email = 1;
          } else if (!this.validateEmail(this.email.email)) {
            this.emailError.emailValid = 1;
            this.emailError.email = 0;
          } else {
            this.emailError.emailValid = 0;
            this.emailError.email = 0;
          }
        } else if (formField === "phone") {
          if (!this.validateTelephone(this.email.phone)) {
            this.emailError.phoneValid = 1;
            this.emailError.phone = 0;
          } else {
            this.emailError.phoneValid = 0;
            this.emailError.phone = 0;
          }
        } else {
          if (
            this.email[formField] == "" ||
            this.email[formField] == null ||
            this.email[formField] == undefined
          ) {
            this.emailError[formField] = 1;
          } else {
            this.emailError[formField] = 0;
          }
        }
      }

      for (var errorField in this.emailError) {
        if (this.emailError[errorField] === 1) {
          this.errorCount = 1;
          return;
        }
      }
      this.errorCount = 0;
      if (!this.errorCount) {
        var formData = this.getEmailFormData();
        this.submitContactUsForm(formData);
      }
    },
    getEmailFormData() {
      var formData = new FormData();
      formData.append("your-name", this.email.name);
      formData.append("your-email", this.email.email);
      formData.append("your-phone", this.email.phone);
      formData.append("your-message", this.email.msg);
      formData.append(
        "emailus_product_name",
        this.$props.formType.toLowerCase() == "setting"
          ? this.$props.diamondData.name
          : getDiamondTitle(this.$props.diamondData)
      );
      formData.append(
        "emailus_product_url",
        this.$props.formType.toLowerCase() == "setting"
          ? this.getSettingPageUrl()
          : this.getDiamondPageUrl()
      );
      return formData;
    },
    getSettingPageUrl() {
      return `${baseUrl}/collections/engagement-rings/settings/${this.$props.diamondData.id}`;
    },
    getDiamondPageUrl() {
      return `${baseUrl}/collections/engagement-rings/diamonds/${this.$props.diamondData.diamondId}/${this.$props.diamondData.diamondType}`;
    },
    submitContactUsForm(contactDetails) {
      this.btnText = "Submitting...";
      setTimeout(() => {
        this.btnText = "Submit";
        this.hideEmailForm = true;
        this.showThankyouDiv = true;
      }, 600);
    },
    validateEmail(emailId) {
      const emailValidator = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      return emailValidator.test(emailId);
    },
    validateTelephone(telephone) {
      const validator = /^([0|\+[0-9]{1,5})?[0-9]{9,15}$/;
      return validator.test(telephone);
    },
  },
};

// waiting loader
const waitLoader = {
  props: ["isLoading", "message"],
  delimiters: ["[[", "]]"],
  template: `#wait-loader`,
  data() {
    return {
      waitingLoader: `${baseAssetsUrl}/loader.gif`,
    };
  },
};

/* diamond email popup */
const diamondConfirmationPopup = {
  components: {
    waitLoader,
  },
  data() {
    return {
      loading: false,
      diamondData: {},
      settingData: {},
      diamondDataName: "",
      diamondDataSKU: "",
    };
  },
  props: [
    "openConfPopup",
    "confirm",
    "cancel",
    "diamond",
    "setting",
    "replaceDiamond",
    "replaceSetting",
    "settingSize",
    "waitLoading",
  ],
  emits: ["closePopup", "updateWaitLoader"],
  delimiters: ["[[", "]]"],
  template: `#diamond-confirmation-popup`,
  watch: {
    replaceSetting() {
      if (this.openConfPopup) {
        if (!!this.$props.replaceSetting) {
          this.settingData = JSON.parse(this.$props.replaceSetting);
        }
      }
    },
    replaceDiamond() {
      if (this.openConfPopup) {
        if (!!this.$props.replaceDiamond) {
          this.diamondData = JSON.parse(this.$props.replaceDiamond);
          this.diamondDataName = `${this.diamondData.caratWeight} Carat ${this.diamondData.shape
            } ${this.diamondData.diamondType == "white" ? "Natural" : "Labgrown"
            } diamond`;
          this.diamondDataSKU = `DIA_${this.diamondData.diamondType == "white" ? "NATURAL" : "LABGROWN"
            }_${this.diamondData.certificateNumber}`;
        }
      }
    },
  },
  methods: {
    confirmReplacing() {
      this.$emit("closePopup");
      this.$emit(
        "updateWaitLoader",
        "true",
        "Your Product is added. You are redirecting to Cart page."
      );
      document.querySelector("#site-wrapper").classList.add("pc-relative");
      window.location.href = `${baseUrl}/cart/`;
    },
    closePopup(e) {
      for (var field in this.email) {
        if (field == "success") {
          this.email[field] = 0;
        } else {
          this.email[field] = "";
        }
      }
      for (var field in this.emailError) {
        this.emailError[field] = 0;
      }
      this.hideEmailForm = false;
      this.showThankyouDiv = false;
      this.$emit("closeEmailPopup");
    },
    setDiamondImage(e, shape) {
      var target = e.target;
      var stage = parseInt(target.dataset.imgErrorStage || "0");
      target.dataset.imgErrorStage = String(stage + 1);
      if (stage === 0) {
        target.setAttribute("src", getShapeFallbackImage(shape));
      } else if (stage === 1) {
        target.setAttribute("src", shapeToImageUrl.DEFAULT);
      }
    },
  },
};

/* diamond image popup */
const RequestDiamondImgPopupComp = {
  data() {
    return {
      errorCount: 0,
      diamond: {
        name: "",
        email: "",
        phone: "",
        msg: "",
        acceptTerms: false,
        success: 0,
      },
      diamondError: {
        name: 0,
        email: 0,
        emailValid: 0,
        phone: 0,
        phoneValid: 0,
        acceptTerms: 0,
      },
    };
  },
  props: [
    "openPopup",
    "diamondData",
    "popupTitle",
    "popupImage",
    "popupPlaceholderImage",
  ],
  emits: ["closeRequestDiamondPopup", "imageError"],
  delimiters: ["[[", "]]"],
  template: `#request-diamond-img-popup`,
  methods: {
    closePopup(e) {
      for (var field in this.diamond) {
        if (field == "success") {
          this.diamond[field] = 0;
        } else {
          this.diamond[field] = "";
        }
      }
      for (var field in this.diamondError) {
        this.diamondError[field] = 0;
      }
      this.$emit("closeRequestDiamondPopup");
    },
    submitForm(e) {
      for (formField in this.diamond) {
        if (formField === "msg" || formField == "success") continue;
        if (formField === "email") {
          if (
            this.diamond.email == "" ||
            this.diamond.email == null ||
            this.diamond.email == undefined
          ) {
            this.diamondError.emailValid = 0;
            this.diamondError.email = 1;
          } else if (!this.validateEmail(this.diamond.email)) {
            this.diamondError.emailValid = 1;
            this.diamondError.email = 0;
          } else {
            this.diamondError.emailValid = 0;
            this.diamondError.email = 0;
          }
        } else if (formField === "phone") {
          if (
            this.diamond.phone == "" ||
            this.diamond.phone == null ||
            this.diamond.phone == undefined
          ) {
            this.diamondError.phoneValid = 0;
            this.diamondError.phone = 1;
          } else if (!this.validateTelephone(this.diamond.phone)) {
            this.diamondError.phoneValid = 1;
            this.diamondError.phone = 0;
          } else {
            this.diamondError.phoneValid = 0;
            this.diamondError.phone = 0;
          }
        } else if (formField == "acceptTerms") {
          if (!this.diamond[formField]) {
            this.diamondError[formField] = 1;
          } else {
            this.diamondError[formField] = 0;
          }
        } else {
          if (
            this.diamond[formField] == "" ||
            this.diamond[formField] == null ||
            this.diamond[formField] == undefined
          ) {
            this.diamondError[formField] = 1;
          } else {
            this.diamondError[formField] = 0;
          }
        }
      }

      for (var errorField in this.diamondError) {
        if (this.diamondError[errorField] === 1) {
          this.errorCount = 1;
          return;
        }
      }
      this.errorCount = 0;

      if (!this.errorCount) {
        this.diamond.success = 1;
        setTimeout(() => {
          this.$emit("closeRequestDiamondPopup");
        }, 3000);
        //action perform to submit diamond image request form form
      }
    },
    validateEmail(emailId) {
      const emailValidator = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      return emailValidator.test(emailId);
    },
    validateTelephone(telephone) {
      const validator = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
      return validator.test(telephone);
    },
  },
};

const WishlistPopupComp = {
  data() {
    return {
      wishlistPopupContent: sessionStorage.getItem("status"),
      status: false,
    };
  },
  props: ["wishlistContent", "itemName", "openPopup"],
  delimiters: ["[[", "]]"],
  template: `#detail-wishlist-popup`,
  computed: {
    getWishlistPageUrl() {
      return `${baseUrl}/wishlist/`;
    },
  },
  watch: {
    openPopup() {
      if (
        this.$props.openPopup &&
        localStorage.getItem("wishlistItems") !== null
      ) {
        this.wishlistPopupContent = sessionStorage.getItem("status");
        this.status = false;
        if (JSON.parse(localStorage.getItem("wishlistItems")).length) {
          if (this.$props.itemName.includes("Diamond")) {
            JSON.parse(localStorage.getItem("wishlistItems")).forEach((v) => {
              if (
                !!sessionStorage.getItem("checkEngageDiaId") &&
                v == sessionStorage.getItem("checkEngageDiaId")
              ) {
                this.wishlistPopupContent = sessionStorage.getItem("status");
                this.status = true;
              }
            });
          } else if (this.$props.itemName.includes("Ring")) {
            JSON.parse(localStorage.getItem("wishlistItems")).forEach((v) => {
              if (v == sessionStorage.getItem("checkSettingId")) {
                this.wishlistPopupContent = sessionStorage.getItem("status");
                this.status = true;
              }
            });
          }
        }
      }
    },
  },
  methods: {
    closeWishlistPopup() {
      this.$emit("closeWishlistMsg");
    },
  },
};

/* add/remove 'pc-header_banner' class from header banner div */
const handleBannerClass = (action = "remove") => {
  if (action == "add") {
    document.getElementById("header-banner").classList.add("pc-header_banner");
  } else {
    document
      .getElementById("header-banner")
      .classList.remove("pc-header_banner");
  }
};

/* page banner component */
const PageBannerComp = {
  props: ["bannerTitle"],
  delimiters: ["[[", "]]"],
  template: `#page-banner`,
};

/* handle page body class */
const handlePageBodyClass = (action, className) => {
  var bodyEle = document.getElementsByTagName("body");
  if (action == "add") {
    bodyEle[0].classList.add(className);
  } else {
    bodyEle[0].classList.remove(className);
  }
};

/* social icons component */
const SocialIconComp = {
  props: ["itemTitle", "itemUrl", "component"],
  emits: ["openEmail", "openTooltip"],
  delimiters: ["[[", "]]"],
  template: `#social-icon-comp`,
  computed: {
    getInstagramLink() {
      return encodeURI(`https://www.instagram.com/ringsofaustralia/`);
      // return encodeURI( `https://www.facebook.com/sharer.php?u=${ this.$props.itemUrl }&t=${ this.$props.itemTitle }` )
    },
    getWhatsAppLink() {
      return encodeURI(
        `https://api.whatsapp.com/send?text=${this.$props.itemTitle} ${this.$props.itemUrl}`
      );
    },
    getPinterestLink() {
      return encodeURI(`https://in.pinterest.com/roaxsmm/_created/`);
      // return encodeURI( `https://www.twitter.com/share?text=${ this.$props.itemTitle }&url=${ this.$props.itemUrl }` )
    },
  },
  methods: {
    clickEmits(e) {
      this.$emit("openEmail", e, "email_us");
    },
    async copyUrl() {
      const el = document.createElement("textarea");
      el.value = window.location.href;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      this.$emit("openTooltip", "show", "copy");

      setTimeout(() => {
        this.$emit("openTooltip", "hide", "copy");
      }, 2000);
      // alert('URL copied to clipboard!');
    },
  },
};

/* ============== diamond functions ================= */

/* get currency wise graphql fields */
const graphQLCurrencyFields = () => {
  var graphQLCurrencyFields = [];
  availableCurrencies.split(",").forEach((curCode) => {
    graphQLCurrencyFields.push(
      `finalPrice${curCode.charAt(0) + curCode.toLowerCase().slice(1)}`
    );
  });
  return graphQLCurrencyFields.join(" ");
};

/* append updated price fields as per requirement */
const appendUpdatedPriceFields = (diamond) => {
  availableCurrencies.split(",").forEach((curCode) => {
    diamond[`finalPrice${curCode}`] =
      diamond[
      `finalPrice${curCode.charAt(0) + curCode.toLowerCase().slice(1)}`
      ];
  });
  return diamond;
};

function toCamelCase(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/* get converted diamond */
const getCurrencyWiseDiamond = (diamond) => {
  // diamond = appendUpdatedPriceFields(diamond)
  diamond["currencySymbol"] = currencySymbol;
  diamond["finalPrice"] = diamond[`finalPrice${toCamelCase(currencyCode)}`];
  // diamond['finalPrice']     = diamond[`finalPrice${currencyCode}`]
  diamond["basePrice"] = diamond[`finalPrice${baseCurrencyCode}`];
  return diamond;
};

/* get currency wise sort field */
const getCurrencyWiseSortField = () => {
  return `final_price_${currencyCode.toLowerCase()}`;
};

/* ============== setting functions ================= */

/* get converted setting */
const getCurrencyWiseSetting = (setting) => {
  setting["currency"] = currencySymbol;
  setting["price"] = setting[`finalPrice${currencyCode}`];
  return setting;
};

/* get converted steps */
const getCurrencyWiseStep = (step) => {
  step.price = `${currencySymbol}${parseFloat(
    step[`finalPrice${currencyCode}`]
  ).toLocaleString()}`;
  return step;
};

// /* get converted sticky bar item */
// const getCurrencyWiseStickyItem = (stickyItem) => {
//     console.log(stickyItem.price);
//     stickyItem.price = `${ currencySymbol }${ parseFloat(stickyItem[`finalPrice${toCamelCase(currencyCode)}`]) }`
//     // stickyItem.price = `${ currencySymbol }${ parseFloat(stickyItem[`finalPriceUSD`]).toLocaleString() }`
//     return stickyItem
// }

// /* get converted sticky bar item */

const getCurrencyWiseStickyItem = (stickyItem) => {
  try {
    const currencyField = `finalPrice${toCamelCase(currencyCode)}`;
    const priceValue = stickyItem[currencyField];
    // Check if the priceValue is valid
    if (
      priceValue !== null &&
      priceValue !== undefined &&
      !isNaN(parseFloat(priceValue))
    ) {
      stickyItem.price = `${currencySymbol}${parseFloat(
        priceValue
      ).toLocaleString()}`;
    } else {
      stickyItem.price = stickyItem.price; // Default to 0.00 if value is invalid
    }

    return stickyItem;
  } catch (error) {
    console.error("Error in getCurrencyWiseStickyItem:", error);
    return stickyItem;
  }
};

/* append currencies to header steps */
const appendCurrenciesToSteps = (headerStep, item) => {
  availableCurrencies.split(",").forEach((currencyCode) => {
    headerStep[`finalPrice${currencyCode}`] = item[`finalPrice${currencyCode}`];
  });
  return headerStep;
};

/* append currencies to sticky bar items */
const appendCurrenciesToStickyBarItems = (stickyItemJSON, stickyItem) => {
  availableCurrencies.split(",").forEach((currencyCode) => {
    stickyItemJSON[`finalPrice${toCamelCase(currencyCode)}`] =
      stickyItem[`finalPrice${toCamelCase(currencyCode)}`];
  });
  return stickyItemJSON;
};

/* append currencies to final Step */
const appendCurrenciesToFinalSteps = (headerStep, diamond, setting) => {
  availableCurrencies.split(",").forEach((currencyCode) => {
    headerStep[`finalPrice${currencyCode}`] =
      parseFloat(diamond[`finalPrice${currencyCode}`]) +
      parseFloat(setting[`finalPrice${currencyCode}`]);
  });
  return headerStep;
};

/* append currencies to recently viewed ringbuilder settings */
const appendCurrenciesToRecentlyViewedSettings = (
  recentSetting,
  curSetting
) => {
  availableCurrencies.split(",").forEach((currencyCode) => {
    recentSetting[`finalPrice${currencyCode}`] =
      curSetting[`finalPrice${currencyCode}`];
  });
  return recentSetting;
};

/* get currency wise recently viewed setting items */
const getCurrencyWiseRecentSetting = (recentSetting) => {
  recentSetting["price"] = `${currencySymbol}${parseFloat(
    recentSetting[`finalPrice${currencyCode}`]
  ).toLocaleString()}`;
  return recentSetting;
};
